import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdActivePaletteService } from '../bodygraph/hd-active-palette.service';

/**
 * Circuit-indicatorkleur per melancholie-poort, gelijk aan de circuit-source-of-
 * truth (`HD_BODYGRAPH_CIRCUITS` in `hd-bodygraph-circuit-metadata.ts`):
 * individueel-weten #00C165, individueel-centrerend #00B9CE, integratie #00522B.
 * Het mystiek circuit (kanaal 25-51) bestaat enkel op deze cheat sheet en heeft
 * geen eigen entry in het circuit-model.
 */
export const HD_MELANCHOLY_MYSTICAL_COLOR = '#9900ff';

export const HD_MELANCHOLY_GATE_COLOR: Readonly<Record<number, string>> = {
  38: '#00C165', 28: '#00C165', 39: '#00C165', 55: '#00C165',
  22: '#00C165', 12: '#00C165', 61: '#00C165', 24: '#00C165', 43: '#00C165', 23: '#00C165',
  60: '#00C165', 3: '#00C165', 14: '#00C165', 2: '#00C165', 1: '#00C165', 8: '#00C165',
  57: '#00C165', 20: '#00C165',
  34: '#00B9CE', 10: '#00B9CE',
  51: '#9900ff', 25: '#9900ff',
};

/** Tweede indicatorkleur (bovenste helft van de split-rail) voor poorten in twee circuits. */
export const HD_MELANCHOLY_GATE_COLOR_2: Readonly<Record<number, string>> = {
  57: '#00522B', 20: '#00522B', 34: '#00522B', 10: '#00522B', // integratie boven
  51: '#00B9CE', 25: '#00B9CE',                               // centrerend boven, mystiek onder
};

interface MelancholyCardVm {
  readonly rail: string;
  readonly title: string;
  readonly keynotes: string;
  readonly iching: string;
  readonly centerIconHtml: SafeHtml | null;
  readonly centerName: string;
}

/**
 * Het melancholie-"blokje" van de Thema's van Melancholie cheat sheet als
 * zelfstandig component: circuit-indicatorkleur als linker rail (split-rail
 * voor poorten in twee circuits), poortnummer(s), het melancholiethema als
 * titel, de twee poort-keynotes, de korte iching-zin en het center-mini-icoon
 * rechtsboven (vervangt de center-kleurvierkantjes van de A3-poster).
 *
 * Met twee poorten (`[gates]="[10, 34]"`) rendert de kaart de kanaal-variant:
 * gestapelde nummers, thema + keynotes uit de `human-design/melancholy`-scope,
 * geen iching-zin en geen center-icoon. Per-poort-thema's komen uit de
 * `human-design/gate-melancholy`-categorie-scope (taalwissel + NL-fallback).
 */
@Component({
  selector: 'hd-melancholy-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'melancholy-card',
    '[attr.data-gates]': 'gates().join("-")',
  },
  template: `
    <span class="melancholy-card__rail" [style.background]="vm().rail"></span>
    <span class="melancholy-card__gates" [class.melancholy-card__gates--channel]="gates().length > 1">
      @for (gate of gates(); track gate) {
        <span class="melancholy-card__gate">{{ gate }}</span>
      }
    </span>
    <div class="melancholy-card__body">
      <p class="melancholy-card__title">{{ vm().title }}</p>
      <p class="melancholy-card__keynotes">{{ vm().keynotes }}</p>
      @if (vm().iching) {
        <p class="melancholy-card__iching">{{ vm().iching }}</p>
      }
    </div>
    @if (vm().centerIconHtml; as icon) {
      <span
        class="melancholy-card__center-icon"
        [innerHTML]="icon"
        [title]="vm().centerName"
        [attr.aria-label]="vm().centerName"
      ></span>
    }
  `,
  styles: `
    :host {
      position: relative;
      display: flex;
      align-items: center;
      gap: 12px;
      background: var(--white);
      border: 1px solid #e6e6e6;
      border-radius: 6px;
      padding: 9px 14px 9px 18px;
      color: #3d414e;
      text-align: left;
    }

    .melancholy-card__rail {
      position: absolute;
      left: 0;
      top: 5px;
      bottom: 5px;
      width: 5px;
      border-radius: 3px;
    }

    .melancholy-card__gates {
      flex: 0 0 auto;
      min-width: 40px;
      display: flex;
      flex-direction: column;
      align-items: center;
      font-family: 'Bw Modelica', 'Raleway', sans-serif;
      font-weight: 900;
      font-size: 26px;
      line-height: 1;
    }

    .melancholy-card__gates--channel {
      font-size: 19px;
      gap: 2px;
    }

    .melancholy-card__body {
      flex: 1 1 auto;
      min-width: 0;
    }

    .melancholy-card__title {
      margin: 0;
      font-weight: 800;
      font-size: 15px;
      line-height: 1.2;
    }

    .melancholy-card__keynotes {
      margin: 2px 0 0;
      font-size: 12px;
      font-weight: 600;
      color: rgba(61, 65, 78, 0.8);
    }

    .melancholy-card__iching {
      margin: 3px 0 0;
      font-size: 11.5px;
      line-height: 1.35;
      color: rgba(61, 65, 78, 0.6);
    }

    .melancholy-card__center-icon {
      flex: 0 0 auto;
      align-self: flex-start;
      display: flex;
      justify-content: flex-end;
      height: 20px;
      width: 26px;
    }
  `,
})
export class HdMelancholyCardComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly centerColors = inject(HdActivePaletteService);

  /** Eén poort (gate-kaart) of twee poorten (kanaal-kaart, bv. `[10, 34]`). */
  readonly gates = input.required<readonly number[]>();

  constructor() {
    // Beide content-talen eager laden zodat de taalwissel én de NL-fallback
    // meteen correcte thema's opleveren (zelfde patroon als hd-fear-card).
    this.gateInfo.loadGateCategoryScope('melancholy', 'nl');
    this.gateInfo.loadGateCategoryScope('melancholy', 'en');
  }

  protected readonly vm = computed<MelancholyCardVm>(() => {
    const gates = this.gates();
    return gates.length > 1 ? this.channelVm(gates) : this.gateVm(gates[0]);
  });

  private gateVm(gate: number): MelancholyCardVm {
    const lang = this.contentLang.effectiveLang();
    const info = this.gateInfo.gateInfoIn(gate, lang);
    const color = HD_MELANCHOLY_GATE_COLOR[gate] ?? '#3d414e';
    const color2 = HD_MELANCHOLY_GATE_COLOR_2[gate];
    const center = info.center;
    return {
      rail: color2 ? `linear-gradient(180deg, ${color2} 50%, ${color} 50%)` : color,
      title: this.categoryKeynote(gate),
      keynotes: [info.translations.keynote1, info.translations.keynote2]
        .filter(part => part !== '')
        .join(', '),
      iching: info.translations.shortIchingDescription,
      centerIconHtml: this.centerColors.iconHtml(center),
      centerName: this.gateInfo.centerLabelsIn(lang)[center] ?? center,
    };
  }

  private channelVm(gates: readonly number[]): MelancholyCardVm {
    const key = gates.join('-');
    return {
      // Kanaal 10-34 hoort bij het individueel-centrerende circuit.
      rail: '#00B9CE',
      title: this.sheetText(`channels.${key}.title`),
      keynotes: this.sheetText(`channels.${key}.keynotes`),
      iching: '',
      centerIconHtml: null,
      centerName: '',
    };
  }

  /** Melancholiethema van een poort uit de categorie-scope, met NL-fallback. */
  private categoryKeynote(gate: number): string {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.gateCategoryInfoIn(gate, 'melancholy', lang)?.keynote ?? '';
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.gateCategoryInfoIn(gate, 'melancholy', 'nl')?.keynote ?? '';
  }

  /** Scope-tekst uit de melancholy cheat-sheet-scope, met NL-fallback. */
  private sheetText(key: string): string {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetTextIn('melancholy', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('melancholy', key, 'nl');
  }
}
